import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Layers,
  Plus,
  Edit,
  Trash2,
  Search,
  Save,
  X,
  Loader2,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Bateria,
  CreateBateria,
  BATERIA_CATEGORIAS,
  BateriaCategoriaArquitetural,
  labelCategoriaBateria,
} from "@/types/baterias";
import { bateriasService } from "@/services/bateriasService";
import { toast } from "sonner";

type BateriaFormValues = {
  marca: string;
  modelo: string;
  tipoCelula: string;
  categoriaArquitetural: BateriaCategoriaArquitetural;
  tensaoNominalV: number;
  capacidadeNominalAh: number;
  energiaKwh: number;
  potenciaNominalKw: number;
  correnteMaxCargaA: number;
  correnteMaxDescargaA: number;
  tensaoOperacaoMinV: number;
  tensaoOperacaoMaxV: number;
  dimensoes: { comprimento: number; largura: number; altura: number };
  pesoKg: number;
  ciclosUteis80Dod: number;
  garantiaAnos: number;
  observacoes: string;
  imagemUrl: string | null;
};

function emptyForm(): BateriaFormValues {
  return {
    marca: "",
    modelo: "",
    tipoCelula: "LiFePO4",
    categoriaArquitetural: "rack_48v",
    tensaoNominalV: 0,
    capacidadeNominalAh: 0,
    energiaKwh: 0,
    potenciaNominalKw: 0,
    correnteMaxCargaA: 0,
    correnteMaxDescargaA: 0,
    tensaoOperacaoMinV: 0,
    tensaoOperacaoMaxV: 0,
    dimensoes: { comprimento: 0, largura: 0, altura: 0 },
    pesoKg: 0,
    ciclosUteis80Dod: 0,
    garantiaAnos: 0,
    observacoes: "",
    imagemUrl: null,
  };
}

function formToCreatePayload(f: BateriaFormValues): CreateBateria {
  return {
    marca: f.marca.trim(),
    modelo: f.modelo.trim(),
    tipoCelula: f.tipoCelula.trim(),
    categoriaArquitetural: f.categoriaArquitetural,
    tensaoNominalV: f.tensaoNominalV,
    capacidadeNominalAh: f.capacidadeNominalAh > 0 ? f.capacidadeNominalAh : null,
    energiaKwh: f.energiaKwh,
    potenciaNominalKw: f.potenciaNominalKw > 0 ? f.potenciaNominalKw : null,
    correnteMaxCargaA: f.correnteMaxCargaA > 0 ? f.correnteMaxCargaA : null,
    correnteMaxDescargaA: f.correnteMaxDescargaA > 0 ? f.correnteMaxDescargaA : null,
    tensaoOperacaoMinV: f.tensaoOperacaoMinV > 0 ? f.tensaoOperacaoMinV : null,
    tensaoOperacaoMaxV: f.tensaoOperacaoMaxV > 0 ? f.tensaoOperacaoMaxV : null,
    dimensoes: { ...f.dimensoes },
    pesoKg: f.pesoKg,
    ciclosUteis80Dod: f.ciclosUteis80Dod > 0 ? f.ciclosUteis80Dod : null,
    garantiaAnos: f.garantiaAnos,
    observacoes: f.observacoes.trim() ? f.observacoes.trim() : null,
    imagemUrl: f.imagemUrl,
  };
}

function bateriaToForm(b: Bateria): BateriaFormValues {
  return {
    marca: b.marca,
    modelo: b.modelo,
    tipoCelula: b.tipoCelula,
    categoriaArquitetural: b.categoriaArquitetural,
    tensaoNominalV: b.tensaoNominalV,
    capacidadeNominalAh: b.capacidadeNominalAh ?? 0,
    energiaKwh: b.energiaKwh,
    potenciaNominalKw: b.potenciaNominalKw ?? 0,
    correnteMaxCargaA: b.correnteMaxCargaA ?? 0,
    correnteMaxDescargaA: b.correnteMaxDescargaA ?? 0,
    tensaoOperacaoMinV: b.tensaoOperacaoMinV ?? 0,
    tensaoOperacaoMaxV: b.tensaoOperacaoMaxV ?? 0,
    dimensoes: { ...b.dimensoes },
    pesoKg: b.pesoKg,
    ciclosUteis80Dod: b.ciclosUteis80Dod ?? 0,
    garantiaAnos: b.garantiaAnos,
    observacoes: b.observacoes ?? "",
    imagemUrl: b.imagemUrl ?? null,
  };
}

export default function Baterias() {
  const permissions = usePermissions();
  const { user, session } = useAuth();
  const isMobile = useIsMobile();
  const canManage = permissions.userRole === "admin" || permissions.userRole === "vendedor";

  const [baterias, setBaterias] = useState<Bateria[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBateria, setEditingBateria] = useState<Bateria | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("marca");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState({
    marca: "",
    categoria: "",
    energiaMin: "",
    energiaMax: "",
  });
  const [formData, setFormData] = useState<BateriaFormValues>(emptyForm);
  const [mesmaCorrenteCargaDescarga, setMesmaCorrenteCargaDescarga] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imagemBateriaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void loadBaterias();
  }, []);

  const loadBaterias = async () => {
    try {
      setIsLoading(true);
      const data = await bateriasService.getAll();
      setBaterias(data);
    } catch (error) {
      console.error("Erro ao carregar baterias:", error);
      toast.error("Erro ao carregar baterias");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    const textFields = new Set(["marca", "modelo", "tipoCelula", "observacoes"]);
    if (textFields.has(field) && typeof value === "string") {
      setFormData((prev) => ({ ...prev, [field]: value }));
      return;
    }

    if (field.startsWith("dimensoes.")) {
      const key = field.split(".")[1] as keyof BateriaFormValues["dimensoes"];
      const num = typeof value === "number" ? value : Number(value);
      setFormData((prev) => ({
        ...prev,
        dimensoes: {
          ...prev.dimensoes,
          [key]: Number.isFinite(num) ? num : 0,
        },
      }));
      return;
    }

    if (typeof value === "string" && value.trim() === "") {
      setFormData((prev) => ({ ...prev, [field]: 0 }));
      return;
    }

    if (typeof value === "number" || !Number.isNaN(Number(value))) {
      const numValue = typeof value === "number" ? value : Number(value);
      setFormData((prev) => {
        const next = { ...prev, [field]: numValue } as BateriaFormValues;
        if (field === "correnteMaxCargaA" && mesmaCorrenteCargaDescarga) {
          next.correnteMaxDescargaA = numValue;
        }
        return next;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !session) {
      toast.error("Usuário não autenticado. Faça login para continuar.");
      return;
    }

    if (!canManage) {
      toast.error("Acesso negado. Apenas administradores e vendedores podem gerenciar baterias.");
      return;
    }

    const payload = formToCreatePayload(formData);

    if (!payload.marca) {
      toast.error("Marca é obrigatória");
      return;
    }
    if (!payload.modelo) {
      toast.error("Modelo é obrigatório");
      return;
    }
    if (!payload.tipoCelula) {
      toast.error("Química / tipo de célula é obrigatório");
      return;
    }
    if (payload.tensaoNominalV <= 0) {
      toast.error("Tensão nominal deve ser maior que zero");
      return;
    }
    if (payload.energiaKwh <= 0) {
      toast.error("Energia (kWh) deve ser maior que zero");
      return;
    }
    if (payload.pesoKg <= 0) {
      toast.error("Peso deve ser maior que zero");
      return;
    }
    if (payload.garantiaAnos <= 0) {
      toast.error("Garantia (anos) deve ser maior que zero");
      return;
    }
    if (
      payload.categoriaArquitetural === "rack_48v" &&
      (payload.capacidadeNominalAh === null || payload.capacidadeNominalAh <= 0)
    ) {
      toast.error("Para rack 48 V, informe a capacidade nominal (Ah)");
      return;
    }
    const hasCorrente =
      (payload.correnteMaxCargaA !== null && payload.correnteMaxCargaA > 0) ||
      (payload.correnteMaxDescargaA !== null && payload.correnteMaxDescargaA > 0);
    const hasPotencia = payload.potenciaNominalKw !== null && payload.potenciaNominalKw > 0;
    if (!hasCorrente && !hasPotencia) {
      toast.error("Informe corrente máx. de carga/descarga ou potência nominal (kW)");
      return;
    }
    if (
      (payload.tensaoOperacaoMinV !== null && payload.tensaoOperacaoMinV > 0) ||
      (payload.tensaoOperacaoMaxV !== null && payload.tensaoOperacaoMaxV > 0)
    ) {
      if (
        !payload.tensaoOperacaoMinV ||
        !payload.tensaoOperacaoMaxV ||
        payload.tensaoOperacaoMinV >= payload.tensaoOperacaoMaxV
      ) {
        toast.error("Faixa de tensão de operação: informe V mín. e V máx. válidos (mín. < máx.)");
        return;
      }
    }

    try {
      setIsSubmitting(true);
      if (editingBateria) {
        let imagemUrl = formData.imagemUrl;
        if (selectedImage) {
          try {
            imagemUrl = await bateriasService.uploadImagem(editingBateria.id, selectedImage);
          } catch (uploadErr) {
            console.error("Erro ao fazer upload da imagem:", uploadErr);
            toast.error("Erro ao fazer upload da imagem");
            return;
          }
        }
        const updated = await bateriasService.update(editingBateria.id, { ...payload, imagemUrl });
        setBaterias((prev) => prev.map((b) => (b.id === editingBateria.id ? updated : b)));
        toast.success("Bateria atualizada com sucesso!");
      } else {
        const created = await bateriasService.create({ ...payload, imagemUrl: null });
        if (selectedImage) {
          try {
            const imgUrl = await bateriasService.uploadImagem(created.id, selectedImage);
            const updated = await bateriasService.update(created.id, { imagemUrl: imgUrl });
            setBaterias((prev) => prev.map((b) => (b.id === created.id ? updated : b)));
            toast.success("Bateria criada com sucesso!");
          } catch (uploadErr) {
            console.error("Erro ao fazer upload da imagem:", uploadErr);
            toast.error("Bateria criada, mas houve erro ao fazer upload da imagem");
            setBaterias((prev) => [...prev, created]);
          }
        } else {
          setBaterias((prev) => [...prev, created]);
          toast.success("Bateria criada com sucesso!");
        }
      }
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Erro ao salvar bateria:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao salvar bateria");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (b: Bateria) => {
    if (!canManage) {
      toast.error("Acesso negado. Apenas administradores e vendedores podem editar baterias.");
      return;
    }
    setEditingBateria(b);
    setFormData(bateriaToForm(b));
    setSelectedImage(null);
    setImagePreview(b.imagemUrl || null);
    const carga = b.correnteMaxCargaA ?? 0;
    const desc = b.correnteMaxDescargaA ?? 0;
    setMesmaCorrenteCargaDescarga(carga > 0 && carga === desc);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!canManage) {
      toast.error("Acesso negado. Apenas administradores e vendedores podem excluir baterias.");
      return;
    }
    if (!confirm("Tem certeza que deseja excluir esta bateria?")) return;
    try {
      await bateriasService.delete(id);
      setBaterias((prev) => prev.filter((b) => b.id !== id));
      toast.success("Bateria excluída com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir bateria:", error);
      toast.error("Erro ao excluir bateria");
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingBateria(null);
    setMesmaCorrenteCargaDescarga(true);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Tamanho máximo: 5MB.");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, imagemUrl: null }));
    if (imagemBateriaInputRef.current) {
      imagemBateriaInputRef.current.value = "";
    }
  };

  const applyFilters = () => {
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setAppliedFilter({ marca: "", categoria: "", energiaMin: "", energiaMax: "" });
    setFilterOpen(false);
  };

  const filtersApplied = Object.values(appliedFilter).some((v) => v !== "" && v !== "todos");

  const uniqueMarcas = [...new Set(baterias.map((b) => b.marca).filter(Boolean))];

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sorted = [...baterias].sort((a, b) => {
    let aVal: string | number = "";
    let bVal: string | number = "";
    switch (sortColumn) {
      case "marca":
        aVal = a.marca || "";
        bVal = b.marca || "";
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case "modelo":
        aVal = a.modelo || "";
        bVal = b.modelo || "";
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case "energiaKwh":
        aVal = a.energiaKwh;
        bVal = b.energiaKwh;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      case "tensaoNominalV":
        aVal = a.tensaoNominalV;
        bVal = b.tensaoNominalV;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      case "pesoKg":
        aVal = a.pesoKg;
        bVal = b.pesoKg;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      default:
        return 0;
    }
  });

  const filtered = sorted.filter((b) => {
    const marca = (b.marca || "").toLowerCase();
    const modelo = (b.modelo || "").toLowerCase();
    const tipo = (b.tipoCelula || "").toLowerCase();
    const q = appliedSearch.toLowerCase();
    const matchesSearch =
      appliedSearch === "" ||
      marca.includes(q) ||
      modelo.includes(q) ||
      tipo.includes(q);

    const matchesMarca =
      appliedFilter.marca === "" ||
      appliedFilter.marca === "todos" ||
      marca === appliedFilter.marca.toLowerCase();
    const matchesCat =
      appliedFilter.categoria === "" ||
      appliedFilter.categoria === "todos" ||
      b.categoriaArquitetural === appliedFilter.categoria;
    const minOk =
      appliedFilter.energiaMin === "" ||
      b.energiaKwh >= Number(appliedFilter.energiaMin);
    const maxOk =
      appliedFilter.energiaMax === "" ||
      b.energiaKwh <= Number(appliedFilter.energiaMax);

    return matchesSearch && matchesMarca && matchesCat && minOk && maxOk;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Carregando baterias...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className={`flex ${isMobile ? "flex-col" : "items-center justify-between"} gap-4`}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
            <Layers className="h-8 w-8" />
            Baterias
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {canManage
              ? "Gerencie o catálogo de baterias disponíveis"
              : "Visualize o catálogo de baterias (apenas leitura)"}
          </p>
        </div>
        <div className={`flex ${isMobile ? "flex-wrap" : "items-center"} gap-2`}>
          <Badge variant="outline">{baterias.length} baterias cadastradas</Badge>
          {!canManage && <Badge variant="secondary">Modo visualização</Badge>}
        </div>
      </div>

      <div className={`flex ${isMobile ? "flex-col" : "items-center justify-between"} gap-3`}>
        <form
          className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-2 flex-1`}
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(searchTerm);
          }}
        >
          <div className={`relative ${isMobile ? "w-full" : "w-96"}`}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por marca, modelo ou química..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className={`flex ${isMobile ? "flex-row" : ""} gap-2`}>
            <Button type="submit" variant="outline" className={isMobile ? "flex-1" : ""}>
              <Search className="h-4 w-4 mr-2" /> Buscar
            </Button>
            <Button
              type="button"
              variant={filtersApplied ? "default" : "outline"}
              onClick={() => setFilterOpen(true)}
              className={isMobile ? "flex-1" : ""}
            >
              <Filter className="h-4 w-4 mr-2" /> Filtros
            </Button>
          </div>
        </form>

        <div className={`flex ${isMobile ? "w-full" : "items-center"} gap-2`}>
          {canManage ? (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => {
                    resetForm();
                  }}
                  className={isMobile ? "w-full" : ""}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar bateria
                </Button>
              </DialogTrigger>
              <DialogContent className={`${isMobile ? "max-w-[95vw]" : "max-w-4xl"} max-h-[90vh] overflow-y-auto`}>
                <DialogHeader>
                  <DialogTitle>{editingBateria ? "Editar bateria" : "Nova bateria"}</DialogTitle>
                  <DialogDescription>
                    Dados técnicos principais conforme datasheet (nem todos os campos da ficha são obrigatórios)
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Identificação</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="marca">Marca *</Label>
                        <Input
                          id="marca"
                          value={formData.marca}
                          onChange={(e) => handleInputChange("marca", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="modelo">Modelo *</Label>
                        <Input
                          id="modelo"
                          value={formData.modelo}
                          onChange={(e) => handleInputChange("modelo", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tipoCelula">Química / tipo de célula *</Label>
                        <Input
                          id="tipoCelula"
                          value={formData.tipoCelula}
                          onChange={(e) => handleInputChange("tipoCelula", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoria arquitetural *</Label>
                        <Select
                          value={formData.categoriaArquitetural}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              categoriaArquitetural: v as BateriaCategoriaArquitetural,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BATERIA_CATEGORIAS.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="imagemBateria">Imagem da bateria</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            ref={imagemBateriaInputRef}
                            id="imagemBateria"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={handleImageChange}
                            className="cursor-pointer max-w-md flex-1 min-w-[200px]"
                          />
                          {(imagePreview || selectedImage) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleRemoveImage}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remover imagem
                            </Button>
                          )}
                        </div>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Pré-visualização"
                              className="w-32 h-32 object-cover rounded border"
                            />
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Formatos aceitos: JPEG, PNG, GIF, WebP. Tamanho máximo: 5MB
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Elétrico</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tensaoNominalV">Tensão nominal (V) *</Label>
                        <Input
                          id="tensaoNominalV"
                          type="number"
                          step="0.1"
                          value={formData.tensaoNominalV || ""}
                          onChange={(e) => handleInputChange("tensaoNominalV", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="energiaKwh">Energia (kWh) *</Label>
                        <Input
                          id="energiaKwh"
                          type="number"
                          step="0.001"
                          value={formData.energiaKwh || ""}
                          onChange={(e) => handleInputChange("energiaKwh", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="capacidadeNominalAh">Capacidade nominal (Ah)</Label>
                        <Input
                          id="capacidadeNominalAh"
                          type="number"
                          step="0.1"
                          value={formData.capacidadeNominalAh || ""}
                          onChange={(e) => handleInputChange("capacidadeNominalAh", Number(e.target.value))}
                        />
                        <p className="text-xs text-muted-foreground">Obrigatório para rack 48 V</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="potenciaNominalKw">Potência nominal carga/descarga (kW)</Label>
                        <Input
                          id="potenciaNominalKw"
                          type="number"
                          step="0.01"
                          value={formData.potenciaNominalKw || ""}
                          onChange={(e) => handleInputChange("potenciaNominalKw", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2 flex items-center gap-2">
                        <Checkbox
                          id="mesmaCorrente"
                          checked={mesmaCorrenteCargaDescarga}
                          onCheckedChange={(c) => {
                            const checked = c === true;
                            setMesmaCorrenteCargaDescarga(checked);
                            if (checked) {
                              setFormData((prev) => ({
                                ...prev,
                                correnteMaxDescargaA: prev.correnteMaxCargaA,
                              }));
                            }
                          }}
                        />
                        <Label htmlFor="mesmaCorrente" className="font-normal cursor-pointer">
                          Mesma corrente máx. para carga e descarga
                        </Label>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="correnteMaxCargaA">Corrente máx. carga (A)</Label>
                        <Input
                          id="correnteMaxCargaA"
                          type="number"
                          step="0.1"
                          value={formData.correnteMaxCargaA || ""}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            if (mesmaCorrenteCargaDescarga) {
                              setFormData((prev) => ({
                                ...prev,
                                correnteMaxCargaA: n,
                                correnteMaxDescargaA: n,
                              }));
                            } else {
                              handleInputChange("correnteMaxCargaA", n);
                            }
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="correnteMaxDescargaA">Corrente máx. descarga (A)</Label>
                        <Input
                          id="correnteMaxDescargaA"
                          type="number"
                          step="0.1"
                          disabled={mesmaCorrenteCargaDescarga}
                          value={formData.correnteMaxDescargaA || ""}
                          onChange={(e) => handleInputChange("correnteMaxDescargaA", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tensaoOperacaoMinV">Tensão operação mín. (V)</Label>
                        <Input
                          id="tensaoOperacaoMinV"
                          type="number"
                          step="0.1"
                          value={formData.tensaoOperacaoMinV || ""}
                          onChange={(e) => handleInputChange("tensaoOperacaoMinV", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tensaoOperacaoMaxV">Tensão operação máx. (V)</Label>
                        <Input
                          id="tensaoOperacaoMaxV"
                          type="number"
                          step="0.1"
                          value={formData.tensaoOperacaoMaxV || ""}
                          onChange={(e) => handleInputChange("tensaoOperacaoMaxV", Number(e.target.value))}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Físico e desempenho</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="comp">Comprimento (mm) *</Label>
                        <Input
                          id="comp"
                          type="number"
                          value={formData.dimensoes.comprimento || ""}
                          onChange={(e) => handleInputChange("dimensoes.comprimento", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="larg">Largura (mm) *</Label>
                        <Input
                          id="larg"
                          type="number"
                          value={formData.dimensoes.largura || ""}
                          onChange={(e) => handleInputChange("dimensoes.largura", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="alt">Altura (mm) *</Label>
                        <Input
                          id="alt"
                          type="number"
                          value={formData.dimensoes.altura || ""}
                          onChange={(e) => handleInputChange("dimensoes.altura", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pesoKg">Peso (kg) *</Label>
                        <Input
                          id="pesoKg"
                          type="number"
                          step="0.1"
                          value={formData.pesoKg || ""}
                          onChange={(e) => handleInputChange("pesoKg", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ciclos">Ciclos úteis</Label>
                        <Input
                          id="ciclos"
                          type="number"
                          value={formData.ciclosUteis80Dod || ""}
                          onChange={(e) => handleInputChange("ciclosUteis80Dod", Number(e.target.value))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="garantia">Garantia (anos) *</Label>
                        <Input
                          id="garantia"
                          type="number"
                          value={formData.garantiaAnos || ""}
                          onChange={(e) => handleInputChange("garantiaAnos", Number(e.target.value))}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Observações</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Label htmlFor="obs" className="sr-only">
                        Compatibilidade e notas
                      </Label>
                      <Textarea
                        id="obs"
                        placeholder="Compatibilidade com inversores, torres em paralelo, etc."
                        value={formData.observacoes}
                        onChange={(e) => handleInputChange("observacoes", e.target.value)}
                        rows={3}
                      />
                    </CardContent>
                  </Card>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {editingBateria ? "Atualizar" : "Salvar"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <span className="text-sm text-muted-foreground">
              Apenas administradores e vendedores podem cadastrar baterias
            </span>
          )}
        </div>
      </div>

      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filtros</DialogTitle>
            <DialogDescription>Refine a lista de baterias</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Marca</Label>
              <Select
                value={appliedFilter.marca || "todos"}
                onValueChange={(v) => setAppliedFilter((p) => ({ ...p, marca: v === "todos" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniqueMarcas.map((m) => (
                    <SelectItem key={m} value={m.toLowerCase()}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={appliedFilter.categoria || "todos"}
                onValueChange={(v) => setAppliedFilter((p) => ({ ...p, categoria: v === "todos" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {BATERIA_CATEGORIAS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>Energia mín. (kWh)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={appliedFilter.energiaMin}
                  onChange={(e) => setAppliedFilter((p) => ({ ...p, energiaMin: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Energia máx. (kWh)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={appliedFilter.energiaMax}
                  onChange={(e) => setAppliedFilter((p) => ({ ...p, energiaMax: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={clearFilters}>
              Limpar
            </Button>
            <Button type="button" onClick={applyFilters}>
              Aplicar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Catálogo</CardTitle>
          <CardDescription>Baterias cadastradas no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground">Nenhuma bateria encontrada.</p>
          ) : isMobile ? (
            <div className="space-y-4">
              {filtered.map((b) => (
                <Card key={b.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex gap-3 items-start">
                      {b.imagemUrl ? (
                        <img
                          src={b.imagemUrl}
                          alt={`${b.marca} ${b.modelo}`}
                          className="w-20 h-20 object-cover rounded shrink-0"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-muted rounded flex items-center justify-center shrink-0">
                          <Layers className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 flex justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-lg">{b.marca}</h3>
                          <p className="text-sm text-muted-foreground truncate">{b.modelo}</p>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                          {b.energiaKwh} kWh
                        </Badge>
                      </div>
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Categoria</span>
                        <span>{labelCategoriaBateria(b.categoriaArquitetural)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">V nom.</span>
                        <span>{b.tensaoNominalV} V</span>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" size="sm" onClick={() => handleEdit(b)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button variant="outline" className="flex-1" size="sm" onClick={() => void handleDelete(b.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Imagem</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("marca")}>
                    Marca {sortColumn === "marca" ? (sortDirection === "asc" ? <ArrowUp className="inline h-4 w-4" /> : <ArrowDown className="inline h-4 w-4" />) : <ArrowUpDown className="inline h-4 w-4 opacity-50" />}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("modelo")}>
                    Modelo
                  </TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("energiaKwh")}>
                    kWh
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("tensaoNominalV")}>
                    V nom.
                  </TableHead>
                  <TableHead>Ah</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("pesoKg")}>
                    kg
                  </TableHead>
                  {canManage && <TableHead className="text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      {b.imagemUrl ? (
                        <img
                          src={b.imagemUrl}
                          alt={`${b.marca} ${b.modelo}`}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                          <Layers className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{b.marca}</TableCell>
                    <TableCell>{b.modelo}</TableCell>
                    <TableCell className="text-sm">{labelCategoriaBateria(b.categoriaArquitetural)}</TableCell>
                    <TableCell>{b.energiaKwh}</TableCell>
                    <TableCell>{b.tensaoNominalV}</TableCell>
                    <TableCell>{b.capacidadeNominalAh ?? "—"}</TableCell>
                    <TableCell>{b.pesoKg}</TableCell>
                    {canManage && (
                      <TableCell className="text-right space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(b)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => void handleDelete(b.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
